import { useState } from 'react';
import {
  Calendar,
  LayoutDashboard,
  Settings,
  MoreHorizontal,
} from 'lucide-react';
import { useNavigation } from '../../context/NavigationContext';
import { useAuth } from '../../auth';
import { usePendingApprovalsCount } from '../../hooks/usePendingApprovalsCount';
import type { TabType } from './Navigation';
import { MoreMenu } from './MoreMenu';
import { cn } from '@/lib/utils';

interface BottomNavItem {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const MAIN_ITEMS: BottomNavItem[] = [
  { id: 'employee', label: 'Meine', icon: LayoutDashboard },
  { id: 'calendar', label: 'Kalender', icon: Calendar },
  { id: 'settings', label: 'Betrieb', icon: Settings },
];

const EMPLOYEE_MAIN_ITEMS: BottomNavItem[] = [
  { id: 'employee', label: 'Übersicht', icon: LayoutDashboard },
];

const MORE_TABS: ReadonlySet<TabType> = new Set(['manager', 'approvals', 'hr', 'orgchart', 'hr-settings', 'help', 'faq']);
const EMPLOYEE_MORE_TABS: ReadonlySet<TabType> = new Set(['help', 'faq']);

export function BottomNav() {
  const { activeTab, setActiveTab } = useNavigation();
  const { isHR, isManager } = useAuth();
  const isEmployee = !isHR && !isManager;
  const [moreOpen, setMoreOpen] = useState(false);
  const pendingCount = usePendingApprovalsCount();

  const mainItems = isEmployee ? EMPLOYEE_MAIN_ITEMS : MAIN_ITEMS;
  const moreTabs = isEmployee ? EMPLOYEE_MORE_TABS : MORE_TABS;
  const isMoreActive = moreTabs.has(activeTab);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setMoreOpen(false);
  };

  return (
    <>
      <MoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onNavigate={handleTabChange}
      />
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-50
                   bg-white dark:bg-gray-900
                   border-t border-gray-200 dark:border-white/10
                   safe-area-bottom no-print"
        aria-label="Hauptnavigation"
      >
        <div className="flex items-center justify-around h-16 px-2">
          {mainItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => handleTabChange(item.id)}
                className={cn(
                  'flex flex-col items-center justify-center gap-0.5 flex-1 py-1',
                  isActive
                    ? 'text-gray-900 dark:text-gray-50'
                    : 'text-gray-400 dark:text-gray-500'
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-medium">{item.label}</span>
              </button>
            );
          })}

          {/* More button */}
          <button
            onClick={() => setMoreOpen(!moreOpen)}
            className={cn(
              'flex flex-col items-center justify-center gap-0.5 flex-1 py-1',
              isMoreActive || moreOpen
                ? 'text-gray-900 dark:text-gray-50'
                : 'text-gray-400 dark:text-gray-500'
            )}
          >
            <div className="relative">
              <MoreHorizontal className="h-5 w-5" />
              {pendingCount > 0 && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-red-500" />
              )}
            </div>
            <span className="text-[10px] font-medium">Mehr</span>
          </button>
        </div>
      </nav>
    </>
  );
}
